<template>
  <header>
    <!-- Offline indicator -->
    <div v-if="!isOnline" class="offline-indicator">
      <span class="offline-icon">⚠️</span>
      <span class="offline-text">You're offline - some features may not work</span>
    </div>

    <!-- start navbar -->
    <div class="navbar navbar-default navbar-inverse">
      <div class="container">
        <div class="navbar-header">
          <button 
            type="button" 
            class="navbar-toggle" 
            :class="{ collapsed: navbarCollapsed }"
            @click="toggleNavbar"
            :aria-expanded="!navbarCollapsed"
          >
            <span class="sr-only">Toggle navigation</span>
            <span class="icon-bar"></span>
            <span class="icon-bar"></span>
            <span class="icon-bar"></span>
          </button>
          <router-link to="/" class="navbar-brand" title="Logo">
            <img src="/images/logo-reg.png" alt="Logo">
          </router-link>
        </div>
        <div class="navbar-collapse" :class="{ collapse: navbarCollapsed, in: !navbarCollapsed }" id="main-navbar">
          <ul class="nav navbar-nav nav-left">
            <li><router-link to="/neighborhoods" title="Neighborhoods">Neighborhoods</router-link></li>
            <li><router-link to="/states" title="States">States</router-link></li>
            <li><router-link to="/regions" title="Regions">Regions</router-link></li>
          </ul>
          <ul class="nav navbar-nav navbar-right">
            <!-- Logged out users -->
            <template v-if="!isAuthenticated">
              <li><a href="#" @click="$emit('show-auth', 'signin')" title="Log In">Log In or</a></li>
              <li><a href="#" @click="$emit('show-auth', 'signup')" title="Sign Up" class="btn-holder"><button type="button" class="btn btn-success">Sign Up</button></a></li>
            </template>
            
            <!-- Logged in users -->
            <template v-else>
              <li class="dropdown" :class="{ open: showDropdown }">
                <a role="button" :title="userAttributes ? `Welcome Back, ${getDisplayName()}` : 'Loading...'" class="dropdown-toggle dropdown-holder" data-toggle="dropdown" href="#" @click="toggleDropdown">
                  <template v-if="userAttributes">
                    Welcome Back,
                    <span class="blue">{{ getDisplayName() }}</span>
                    <img :src="profilePictureUrl || '/images/default-avatar-silhouette.svg'" alt="Avatar" class="profile-avatar" @error="handleImageError">
                  </template>
                  <template v-else>
                    <span class="loading-user-info">Loading...</span>
                  </template>
                  <b class="caret caret-blue"></b>
                </a>
                <ul class="dropdown-menu" role="menu" v-show="showDropdown">
                  <li role="presentation">
                    <router-link role="menuitem" tabindex="-1" to="/profile" @click="showDropdown = false">Profile</router-link>
                    <a role="menuitem" tabindex="-1" href="#" @click="$emit('sign-out')">Log Out</a>
                  </li>
                </ul>
              </li>
              <li>
                <a href="#" class="btn-holder" title="Share">
                  <button type="button" class="btn btn-success">Share</button>
                </a>
              </li>
            </template>
          </ul>
        </div>
      </div>
    </div>
    <!-- end navbar -->
    <!-- Landing page background image -->
    <div v-if="showBackground" class="bgd-image"></div>
  </header>
</template>

<script>
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth'
import { getUserName, getUserPicture } from '../utils/userUtils.js'
import { useNetworkStatus } from '../composables/useNetworkStatus.js'

export default {
  name: 'AppHeader',
  props: {
    isAuthenticated: {
      type: Boolean,
      required: true
    },
    showBackground: {
      type: Boolean,
      default: false
    },
    userAttributes: {
      type: Object,
      default: null
    }
  },
  emits: ['show-auth', 'sign-out'],
  setup() {
    // Network status monitoring
    const { isOnline, connectionType } = useNetworkStatus()

    return {
      isOnline,
      connectionType
    }
  },
  data() {
    return {
      showDropdown: false,
      user: null,
      navbarCollapsed: true,
      profilePictureUrl: null
    }
  },
  async mounted() {
    if (this.isAuthenticated) {
      await this.loadUserInfo()
      await this.loadProfilePicture()
    }
  },
  watch: {
    isAuthenticated: {
      immediate: true,
      async handler(newValue) {
        if (newValue) {
          await this.loadUserInfo()
          await this.loadProfilePicture()
        } else {
          this.user = null
          this.profilePictureUrl = null
        }
      }
    },
    userAttributes: {
      immediate: true,
      async handler(newValue) {
        if (newValue && this.isAuthenticated) {
          await this.loadProfilePicture()
        }
      }
    }
  },
  methods: {
    async loadUserInfo() {
      try {
        this.user = await getCurrentUser()
        // userAttributes now comes from props (cached in AuthWrapper)
      } catch (error) {
        console.error('Error getting user info:', error)
      }
    },
    async loadProfilePicture() {
      try {
        const userObj = this.userAttributes ? { attributes: this.userAttributes } : this.user;
        if (!userObj) return;
        
        const pictureResult = getUserPicture(userObj);
        
        if (pictureResult && typeof pictureResult === 'object' && typeof pictureResult.then === 'function') {
          // It's a Promise, wait for it
          this.profilePictureUrl = await pictureResult;
        } else {
          // It's a regular URL
          this.profilePictureUrl = pictureResult;
        }
      } catch (error) {
        this.profilePictureUrl = '/images/default-avatar-silhouette.svg';
      }
    },
    toggleDropdown() {
      console.log('toggleDropdown called, current state:', this.showDropdown)
      this.showDropdown = !this.showDropdown
      console.log('toggleDropdown new state:', this.showDropdown)
    },
    toggleNavbar() {
      this.navbarCollapsed = !this.navbarCollapsed
    },
    getDisplayName() {
      // Use shared utility with fallback to userAttributes or user object
      const userObj = this.userAttributes ? { attributes: this.userAttributes } : this.user
      return getUserName(userObj)
    },
    getDisplayPicture() {
      // Use shared utility with fallback to userAttributes or user object
      const userObj = this.userAttributes ? { attributes: this.userAttributes } : this.user
      return getUserPicture(userObj)
    },
    
    handleImageError(event) {
      event.target.src = '/images/default-avatar-silhouette.svg'
    }
  }
}
</script>

<style scoped>
/* Navbar brand styles - uses pages.css utilities */

/* Header styles - uses pages.css utilities */
header {
  position: relative;
}

.bgd-image {
  position: relative;
  z-index: 1;
}

/* Offline indicator styles */
.offline-indicator {
  background-color: #f59e0b;
  color: white;
  padding: 8px 16px;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 1000;
}

.offline-icon {
  font-size: 18px;
}

.offline-text {
  font-size: 14px;
}

@media (max-width: 768px) {
  .offline-indicator {
    font-size: 12px;
    padding: 6px 12px;
  }

  .offline-icon {
    font-size: 16px;
  }

  .offline-text {
    font-size: 12px;
  }
}

/* Profile dropdown styles */
.profile-avatar {
  width: 20px;
  height: 20px;
  margin-left: var(--spacing-xs);
  border-radius: 50%;
}

.blue {
  color: var(--color-primary);
}

.dropdown-menu {
  background-color: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-sm);
  box-shadow: var(--shadow-lg);
  min-width: 160px;
  padding: var(--spacing-xs) 0;
  margin: var(--spacing-xs) 0 0;
  position: absolute;
  top: 100%;
  right: 0;
  z-index: var(--z-index-dropdown);
}

.dropdown-menu a,
.dropdown-menu router-link {
  color: var(--color-text-dark);
  display: block;
  padding: var(--spacing-xs) var(--spacing-lg);
  text-decoration: none;
  white-space: nowrap;
}

.dropdown-menu a:hover,
.dropdown-menu router-link:hover {
  background-color: var(--color-background-light);
  color: var(--color-text-dark);
}

.caret-blue {
  margin-left: var(--spacing-xs);
}

.dropdown-holder {
  position: relative;
}

.loading-user-info {
  color: var(--color-text-light);
  font-style: italic;
  opacity: 0.7;
}

/* Mobile responsive styles */
@media (max-width: 767px) {
  .nav-left {
    margin-top: 60px;
  }

  .navbar-right > li {
    display: inline-block;
    float: none;
  }

  .dropdown-menu {
    position: static !important;
    float: none;
    width: auto;
    margin-top: 0;
    background-color: transparent;
    border: 0;
    box-shadow: none;
    padding: 0;
  }

  .dropdown-menu > li > a {
    color: var(--color-text-light);
    padding: var(--spacing-xs) var(--spacing-md) var(--spacing-xs) var(--spacing-xl);
  }

  .dropdown-menu > li > a:hover {
    color: var(--color-background);
    background-color: transparent;
  }
}
</style>